import { useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import {
  FolderOpen,
  FileText,
  Image as ImageIcon,
  Download,
  ChevronRight,
  HardDrive,
} from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { asList, parseDateObj, buildChartIndex, chipStyles } from '../../lib/chart-helpers.js'
import { usePatientLive } from '../../lib/patientLive.js'
import { fetchAllTreatments } from '../../lib/billing.js'
import { DOC_TYPE_LABEL, normalizeDocType } from '../../components/DocumentEditor.jsx'
import { isBillingDoc } from '../../components/DocumentGenerator.jsx'
import { useMe } from '../../lib/me.js'
import { isEmployee } from '../../lib/role.js'
import {
  DataTable, useTableState, ViewToggle, Segmented, useFilters,
  Loading, EmptyState, NoMatchState, ErrorState,
  dateParts, fmtDate, fmtMonth, fmtBytes,
} from '../../kit'

export default function PatientChartFile() {
  const { id } = useParams()
  const { live } = useOutletContext() || {}
  // Bumped by a realtime event → refetches in place.
  const [tick, setTick] = useState(0)
  const [treatments, setTreatments] = useState(null)
  const [charts, setCharts]         = useState(null)
  const [error, setError]           = useState(null)
  // Per patient — a doc type one patient has may not exist for the next.
  const [typeFilter, setTypeFilter] = useState('__all')
  // รูปย่อ (thumbnail grid) or ตาราง (table) — remembered per browser. No
  // patient data in this key (filters are browser storage).
  const { filters: viewPref, set: setViewPref } = useFilters('patient-chart-file', { view: 'grid' })
  const view = viewPref.view === 'table' ? 'table' : 'grid'
  const me = useMe()
  const showBilling = isEmployee(me)

  // Live: a document attached or deleted writes chart.document[]; a new visit
  // writes both rows. Refetch WITHOUT clearing, so the list does not blink while
  // someone is reading it.
  usePatientLive(live, ['chart', 'treatment'], () => setTick((t) => t + 1))

  useEffect(() => { setTreatments(null); setCharts(null); setError(null); setTypeFilter('__all') }, [id])

  useEffect(() => {
    let alive = true
    setError(null)
    Promise.allSettled([
      fetchAllTreatments(id),
      apiGet('/api/charts', { patient_id: id }),
    ]).then(([tRes, cRes]) => {
      if (!alive) return
      if (tRes.status === 'fulfilled') setTreatments(tRes.value)
      else { setError(tRes.reason?.message || 'โหลด treatment ไม่สำเร็จ'); setTreatments([]) }
      if (cRes.status === 'fulfilled') setCharts(asList(cRes.value))
      else setCharts([])
    })
    return () => { alive = false }
  }, [id, tick])

  const chartIndex = useMemo(() => buildChartIndex(charts || []), [charts])

  // Flatten every chart.document[] across all visits into one list, each item
  // carrying its visit context (date + appointment type).
  const files = useMemo(() => {
    const out = []
    for (const t of treatments || []) {
      const chart = chartIndex.findFor(t)
      const docs = Array.isArray(chart?.document) ? chart.document : []
      docs.forEach((doc, docIndex) => {
        // Billing docs (ใบเสร็จ/ใบแจ้งหนี้/รายละเอียดค่ารักษา) are hidden from
        // non-employee (doctor-only) sessions — they expose pricing.
        if (!showBilling && isBillingDoc(doc)) return
        const when = doc.created_at ? new Date(doc.created_at) : parseDateObj(t.date)
        out.push({ doc, docIndex, treatment: t, chart, when })
      })
    }
    out.sort((a, b) => (b.when?.getTime() ?? 0) - (a.when?.getTime() ?? 0))
    return out
  }, [treatments, chartIndex, showBilling])

  // Type filter — only types actually present, plus "untyped".
  const typeOptions = useMemo(() => {
    const counts = new Map()
    for (const f of files) {
      const key = normalizeDocType(f.doc?.doc_type) || '__none'
      counts.set(key, (counts.get(key) || 0) + 1)
    }
    const opts = [{ value: '__all', label: 'ทั้งหมด', count: files.length }]
    for (const [key, count] of counts) {
      opts.push({ value: key, label: key === '__none' ? 'ไม่ระบุประเภท' : (DOC_TYPE_LABEL[key] || key), count })
    }
    return opts
  }, [files])

  const filtered = useMemo(() => {
    if (typeFilter === '__all') return files
    return files.filter((f) => (normalizeDocType(f.doc?.doc_type) || '__none') === typeFilter)
  }, [files, typeFilter])

  const groups = useMemo(() => groupByMonth(filtered), [filtered])
  const columns = useMemo(() => fileColumns(id), [id])
  const table = useTableState(filtered, columns, { pageSize: 50, sort: { key: 'when', dir: 'desc' } })
  const totalBytes = useMemo(() => files.reduce((acc, f) => acc + (Number(f.doc?.size) || 0), 0), [files])

  const loading = treatments === null || charts === null
  const retry = () => { setError(null); setTreatments(null); setCharts(null); setTick((t) => t + 1) }

  return (
    <div>
      <p className="text-[14px] text-slate-500 mb-5">เอกสารและไฟล์ทั้งหมดของผู้ป่วย — รวมจากทุก visit</p>

      {loading && <Loading />}
      {!loading && error && <ErrorState message={error} onRetry={retry} />}
      {!loading && !error && files.length === 0 && (
        <EmptyState icon={FolderOpen} title="ยังไม่มีไฟล์ในเคสใด ๆ" hint="ไฟล์ที่อัปโหลดในแต่ละ visit จะรวมแสดงที่นี่" />
      )}

      {!loading && !error && files.length > 0 && (
        <div className="space-y-5">
          {/* Stats */}
          <div className="flex items-center gap-4 text-[13.5px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <FolderOpen size={13} className="text-brand-500" strokeWidth={2} />
              <span className="font-semibold text-slate-700 tabular-nums">{files.length}</span> ไฟล์
            </span>
            <span className="inline-flex items-center gap-1.5">
              <HardDrive size={13} className="text-slate-400" strokeWidth={2} />
              <span className="font-semibold text-slate-700 tabular-nums">{fmtBytes(totalBytes)}</span>
            </span>
            <div className="ml-auto"><ViewToggle value={view} onChange={(v) => setViewPref('view', v)} views={['grid', 'table']} /></div>
          </div>

          {/* Type filter — scrolls sideways when a patient has many doc types. */}
          <Segmented value={typeFilter} onChange={setTypeFilter} options={typeOptions} scroll className="pb-1" />

          {filtered.length === 0 && <NoMatchState variant="card" onClear={() => setTypeFilter('__all')} />}

          {view === 'table' && filtered.length > 0 && (
            <DataTable
              columns={columns}
              rowKey={(f) => `${f.treatment.id ?? f.treatment.treatment_id}-${f.docIndex}`}
              {...table.tableProps}
            />
          )}

          {/* Month-grouped grid */}
          {view === 'grid' && groups.map((g) => (
            <section key={g.key}>
              <div className="flex items-center gap-3 mb-3 px-1">
                <h3 className="text-[14px] font-semibold text-slate-700 whitespace-nowrap">{fmtMonth(g.iso, { long: true })}</h3>
                <span className="text-[13px] text-slate-400 font-medium whitespace-nowrap">{g.items.length} ไฟล์</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {g.items.map((f) => (
                  <FileCard
                    key={`${f.treatment.id ?? f.treatment.treatment_id}-${f.docIndex}`}
                    patientId={id}
                    item={f}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* File card                                                                  */
/* -------------------------------------------------------------------------- */

// Everything both views derive from one file — the card and the table row must
// open the same URL and show the same labels.
function fileMeta(item) {
  const { doc, treatment: t } = item
  const name      = doc.name || 'untitled'
  const mime      = doc.type || doc.mime || ''
  const location  = doc.location || doc.key || ''
  const fullUrl   = location ? `/api/files/${encodeKey(location)}` : (doc.url || '')
  const thumbUrl  = doc.thumbnail ? `/api/files/${encodeKey(doc.thumbnail)}` : ''
  const isImage   = /^image\//.test(mime) || /\.(png|jpe?g|webp|gif|heic)$/i.test(name)
  const previewUrl = thumbUrl || (isImage ? fullUrl : '')
  const tid = t.id ?? t.treatment_id
  const al  = t.appointmentlist || t.appointmentList || {}
  const alType = al.type || t.type || ''
  const alColor = al.color
  const typeLabel = doc.doc_type ? (DOC_TYPE_LABEL[normalizeDocType(doc.doc_type)] || doc.doc_type) : ''
  return { name, fullUrl, isImage, previewUrl, tid, alType, alColor, typeLabel }
}

function FileCard({ patientId, item }) {
  const { doc, treatment: t, chart, when } = item
  const { name, fullUrl, isImage, previewUrl, tid, alType, alColor, typeLabel } = fileMeta(item)

  return (
    <div className="group rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-md hover:shadow-slate-900/5 hover:border-slate-300 transition-all">
      {/* Preview — opens the file in a new tab */}
      <a
        href={fullUrl || undefined}
        target={fullUrl ? '_blank' : undefined}
        rel="noopener noreferrer"
        className="relative block aspect-square bg-slate-50 flex items-center justify-center overflow-hidden"
      >
        {previewUrl
          ? <img src={previewUrl} alt={name} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
          : (isImage
              ? <ImageIcon size={26} className="text-slate-300" strokeWidth={1.5} />
              : <FileText size={26} className="text-slate-300" strokeWidth={1.5} />)}
        {typeLabel && (
          <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-[11.5px] font-semibold bg-white/90 text-slate-600 ring-1 ring-slate-200 backdrop-blur">
            {typeLabel}
          </span>
        )}
        <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md bg-white/90 ring-1 ring-slate-200 text-slate-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Download size={12} strokeWidth={2} />
        </span>
      </a>

      {/* Meta */}
      <div className="p-2.5">
        <div className="text-[13.5px] font-medium text-slate-800 truncate" title={name}>{name}</div>
        <div className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-1.5">
          {when && <span className="tabular-nums">{fmtDate(when)}</span>}
          {doc.size ? <><span>·</span><span className="tabular-nums">{fmtBytes(doc.size)}</span></> : null}
        </div>
        <Link
          to={`/patient/${patientId}/chart/history/${tid}`}
          state={{ treatment: t, chart }}
          className="mt-2 inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-brand-700 transition-colors max-w-full"
        >
          {alType ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[12px] font-semibold truncate" style={chipStyles(alColor)}>
              {alType}
            </span>
          ) : <span>เปิด visit</span>}
          <ChevronRight size={11} className="flex-none" />
        </Link>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Table view                                                                 */
/* -------------------------------------------------------------------------- */

// Kit DataTable columns. Every value comes from fileMeta, so a row opens the
// same URL and shows the same labels as its card.
function fileColumns(patientId) {
  return [
    {
      key: 'name', label: 'ชื่อไฟล์', width: '45%', className: 'max-w-0', sortable: true,
      sortValue: (f) => String(f.doc?.name || '').toLowerCase(),
      render: (f) => {
        const { name, fullUrl, isImage, previewUrl, typeLabel } = fileMeta(f)
        return (
          <a {...openProps(fullUrl)} className="flex items-center gap-2.5 min-w-0 group">
            <span className="flex-none w-9 h-9 rounded-md bg-slate-50 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center">
              {previewUrl
                ? <img src={previewUrl} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
                : (isImage
                    ? <ImageIcon size={16} className="text-slate-300" strokeWidth={1.75} />
                    : <FileText size={16} className="text-slate-300" strokeWidth={1.75} />)}
            </span>
            <span className="min-w-0">
              <span className="block text-[14px] font-medium text-slate-800 truncate group-hover:text-brand-700" title={name}>{name}</span>
              {typeLabel && <span className="md:hidden block text-[12px] text-slate-400 truncate">{typeLabel}</span>}
            </span>
          </a>
        )
      },
    },
    {
      key: 'type', label: 'ประเภท', hideBelow: 'md', sortable: true, className: 'whitespace-nowrap',
      sortValue: (f) => String(fileMeta(f).typeLabel || '').toLowerCase(),
      render: (f) => fileMeta(f).typeLabel || <span className="text-slate-300">—</span>,
    },
    {
      key: 'visit', label: 'Visit', sortable: true,
      sortValue: (f) => String(fileMeta(f).alType || '').toLowerCase(),
      render: (f) => {
        const { tid, alType, alColor } = fileMeta(f)
        return (
          <Link
            to={`/patient/${patientId}/chart/history/${tid}`}
            state={{ treatment: f.treatment, chart: f.chart }}
            className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-brand-700 transition-colors max-w-[180px]"
          >
            {alType
              ? <span className="px-1.5 py-0.5 rounded text-[12px] font-semibold truncate" style={chipStyles(alColor)}>{alType}</span>
              : <span>เปิด visit</span>}
            <ChevronRight size={11} className="flex-none" />
          </Link>
        )
      },
    },
    {
      key: 'when', label: 'วันที่', sortable: true, defaultDir: 'desc', className: 'tabular-nums whitespace-nowrap',
      sortValue: (f) => f.when?.getTime() ?? 0,
      render: (f) => fmtDate(f.when),
    },
    {
      key: 'size', label: 'ขนาด', align: 'right', hideBelow: 'sm', sortable: true, defaultDir: 'desc', className: 'whitespace-nowrap',
      sortValue: (f) => Number(f.doc?.size) || 0,
      render: (f) => (f.doc?.size ? fmtBytes(f.doc.size) : '—'),
    },
    {
      key: 'open', label: '', width: '40px', align: 'right',
      render: (f) => {
        const { fullUrl } = fileMeta(f)
        return fullUrl ? (
          <a {...openProps(fullUrl)} title="เปิดไฟล์" aria-label="เปิดไฟล์" className="inline-flex w-7 h-7 rounded-md text-slate-400 hover:text-brand-700 hover:bg-slate-100 items-center justify-center">
            <Download size={14} strokeWidth={2} />
          </a>
        ) : null
      },
    },
  ]
}

const openProps = (url) => (url ? { href: url, target: '_blank', rel: 'noopener noreferrer' } : {})

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// Month groups for the grid, by the file's Bangkok calendar month (kit dateParts).
function groupByMonth(items) {
  const map = new Map()
  const out = []
  for (const it of items) {
    const p = it.when && !isNaN(it.when.getTime()) ? dateParts(it.when) : null
    if (!p) continue
    const key = `${p.y}-${String(p.m).padStart(2, '0')}`
    if (!map.has(key)) {
      const g = { key, iso: `${key}-01`, year: p.y, month: p.m, items: [] }
      map.set(key, g)
      out.push(g)
    }
    map.get(key).items.push(it)
  }
  out.sort((a, b) => (b.year - a.year) || (b.month - a.month))
  return out
}

function encodeKey(k) {
  return String(k).split('/').map(encodeURIComponent).join('/')
}
